import {
 useCallback, useEffect, useRef, useState,
} from 'react';

export const STATUS = {
  IDLE: 'idle',
  REQUESTING: 'requesting',
  DENIED: 'denied',
  UNSUPPORTED: 'unsupported',
  READY: 'ready',
  COUNTDOWN: 'countdown',
  RECORDING: 'recording',
  PREVIEW: 'preview',
};

const COUNTDOWN_FROM = 3;

// Chrome/Firefox record WebM; Safari only records MP4. Cloudinary accepts either and serves an MP4
// derivative for playback, so the first supported type wins. "" lets the browser pick its default.
const MIME_CANDIDATES = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4', ''];

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  const supported = MIME_CANDIDATES.find((type) => type === '' || MediaRecorder.isTypeSupported(type));
  return supported === undefined ? null : supported;
};

// Owns the entire getUserMedia/MediaRecorder lifecycle so the modal stays a thin presentational shell
// and every teardown path lives in one place. Acquires the camera when `show` is true (never on mount).
export default function useVideoRecorder({
 show, maxSeconds = 120, onRecorded, onHide,
}) {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [countdownValue, setCountdownValue] = useState(COUNTDOWN_FROM);
  // Non-null while the 3-2-1 before a paused recording resumes is running (recorder stays paused).
  const [resumeCountdownValue, setResumeCountdownValue] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [errorKind, setErrorKind] = useState(null);

  const [cameras, setCameras] = useState([]);
  const [mics, setMics] = useState([]);
  const [cameraId, setCameraId] = useState(null);
  const [micId, setMicId] = useState(null);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [paused, setPaused] = useState(false);
  // Mirrors of the above read from async code (getUserMedia callbacks) that would otherwise see stale state.
  const cameraIdRef = useRef(null);
  const micIdRef = useRef(null);
  const cameraOnRef = useRef(true);
  const micOnRef = useRef(true);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedBlobRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeTypeRef = useRef('');
  const objectUrlRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const resumeCountdownTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const livePreviewRef = useRef(null);
  // Elapsed mirror for rescheduling the auto-stop after a pause; discard flag so restart's stop()
  // skips the preview; beginCountdown ref to break the start<->countdown useCallback cycle.
  const elapsedSecondsRef = useRef(0);
  const pausedRef = useRef(false);
  const discardingRef = useRef(false);
  const beginCountdownRef = useRef(null);
  const acquireRef = useRef(null);
  const zombieRetriesRef = useRef(0);
  // Bumped on every teardown/acquire so a getUserMedia promise that resolves after we've moved on
  // stops its orphaned stream instead of resurrecting a dead session.
  const acquireSeqRef = useRef(0);

  const clearResumeCountdown = useCallback(() => {
    if (resumeCountdownTimerRef.current) {
      clearInterval(resumeCountdownTimerRef.current);
      resumeCountdownTimerRef.current = null;
    }
    setResumeCountdownValue(null);
  }, []);

  const clearTimers = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (resumeCountdownTimerRef.current) {
      clearInterval(resumeCountdownTimerRef.current);
      resumeCountdownTimerRef.current = null;
    }
    setResumeCountdownValue(null);
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    acquireSeqRef.current += 1;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (livePreviewRef.current) {
      livePreviewRef.current.srcObject = null;
    }
  }, []);

  // iOS Safari only honors inline autoplay when the muted/playsinline ATTRIBUTES are on the
  // element — React only sets the muted property (facebook/react#22045) — and even then wants an
  // explicit play(). Without this the stream attaches but never renders: a black live preview.
  const attachLivePreview = useCallback((node, stream) => {
    if (!node) return;
    /* eslint-disable no-param-reassign -- imperative <video> setup is the API */
    node.muted = true;
    node.setAttribute('muted', '');
    node.setAttribute('playsinline', '');
    node.srcObject = stream;
    /* eslint-enable no-param-reassign */
    node.play()?.catch(() => {}); // rejection = autoplay blocked; retried by the next effect run
  }, []);

  const revokeUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const teardownAll = useCallback(() => {
    clearTimers();
    stopStream();
    revokeUrl();
    recorderRef.current = null;
    recordedBlobRef.current = null;
    chunksRef.current = [];
  }, [clearTimers, stopStream, revokeUrl]);

  const resetState = useCallback(() => {
    setStatus(STATUS.IDLE);
    setCountdownValue(COUNTDOWN_FROM);
    setElapsedSeconds(0);
    elapsedSecondsRef.current = 0;
    setPaused(false);
    pausedRef.current = false;
    discardingRef.current = false;
    setRecordedUrl(null);
    setErrorKind(null);
    // Reset toggles/device picks so a re-open detects defaults (a picked device may be gone).
    setCameraOn(true);
    setMicOn(true);
    cameraOnRef.current = true;
    micOnRef.current = true;
    setCameraId(null);
    setMicId(null);
    cameraIdRef.current = null;
    micIdRef.current = null;
  }, []);

  // Constraints honor the picked devices once chosen; `true` lets the browser use its default.
  const mediaConstraints = () => ({
    video: cameraIdRef.current ? { deviceId: { exact: cameraIdRef.current } } : true,
    audio: micIdRef.current ? { deviceId: { exact: micIdRef.current } } : true,
  });

  // Apply the current mute/camera-off toggles to a (possibly just-acquired) stream's tracks.
  const applyToggles = (stream) => {
    stream.getVideoTracks().forEach((track) => {
      // eslint-disable-next-line no-param-reassign -- toggling a track's enabled flag is the API
      track.enabled = cameraOnRef.current;
    });
    stream.getAudioTracks().forEach((track) => {
      // eslint-disable-next-line no-param-reassign -- toggling a track's enabled flag is the API
      track.enabled = micOnRef.current;
    });
  };

  // Labels are only populated after permission is granted, so we enumerate post-acquire. Default the
  // dropdown selections to whatever devices the active tracks landed on.
  const refreshDevices = useCallback(async (stream) => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const all = await navigator.mediaDevices.enumerateDevices();
    setCameras(all.filter((d) => d.kind === 'videoinput'));
    setMics(all.filter((d) => d.kind === 'audioinput'));
    const vId = stream?.getVideoTracks()[0]?.getSettings?.().deviceId;
    const aId = stream?.getAudioTracks()[0]?.getSettings?.().deviceId;
    if (vId && !cameraIdRef.current) {
      cameraIdRef.current = vId;
      setCameraId(vId);
    }
    if (aId && !micIdRef.current) {
      micIdRef.current = aId;
      setMicId(aId);
    }
  }, []);

  const acquire = useCallback(async () => {
    const mimeType = pickMimeType();
    if (mimeType === null || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setStatus(STATUS.UNSUPPORTED);
      return;
    }
    mimeTypeRef.current = mimeType;
    setErrorKind(null);
    setStatus(STATUS.REQUESTING);
    acquireSeqRef.current += 1;
    const seq = acquireSeqRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia(mediaConstraints());
      if (seq !== acquireSeqRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      applyToggles(stream);
      setStatus(STATUS.READY);
      refreshDevices(stream);
      // iOS camera-handoff zombie: on quick reopen, the track reports "live" but never delivers
      // frames (element stuck at readyState<3 / currentTime 0) -> black preview. Detect and
      // silently re-acquire after a beat so the camera session fully releases first.
      setTimeout(() => {
        if (seq !== acquireSeqRef.current) return;
        const node = livePreviewRef.current;
        const track = streamRef.current?.getVideoTracks?.()[0];
        const starved = node && track && track.readyState === 'live'
          && (node.readyState < 3 || node.currentTime === 0);
        if (!starved) {
          zombieRetriesRef.current = 0;
          return;
        }
        if (zombieRetriesRef.current >= 2) return;
        zombieRetriesRef.current += 1;
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setTimeout(() => {
          if (seq === acquireSeqRef.current) acquireRef.current?.();
        }, 450);
      }, 1600);
    } catch (err) {
      if (seq !== acquireSeqRef.current) return;
      setErrorKind(err?.name || 'error');
      const denied = err?.name === 'NotAllowedError' || err?.name === 'SecurityError';
      setStatus(denied ? STATUS.DENIED : STATUS.UNSUPPORTED);
    }
  }, [refreshDevices]);
  acquireRef.current = acquire;

  // Swap the whole stream when a device is picked (setup only). Reattaches the preview and reapplies toggles.
  const reacquireWithDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    acquireSeqRef.current += 1;
    const seq = acquireSeqRef.current;
    const stream = await navigator.mediaDevices.getUserMedia(mediaConstraints());
    if (seq !== acquireSeqRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    streamRef.current = stream;
    applyToggles(stream);
    attachLivePreview(livePreviewRef.current, stream);
  }, [attachLivePreview]);

  const selectCamera = useCallback(
    (id) => {
      cameraIdRef.current = id;
      setCameraId(id);
      reacquireWithDevices();
    },
    [reacquireWithDevices],
  );

  const selectMic = useCallback(
    (id) => {
      micIdRef.current = id;
      setMicId(id);
      reacquireWithDevices();
    },
    [reacquireWithDevices],
  );

  const toggleCamera = useCallback(() => {
    const next = !cameraOnRef.current;
    cameraOnRef.current = next;
    setCameraOn(next);
    streamRef.current?.getVideoTracks().forEach((track) => {
      // eslint-disable-next-line no-param-reassign -- toggling a track's enabled flag is the API
      track.enabled = next;
    });
  }, []);

  const toggleMic = useCallback(() => {
    const next = !micOnRef.current;
    micOnRef.current = next;
    setMicOn(next);
    streamRef.current?.getAudioTracks().forEach((track) => {
      // eslint-disable-next-line no-param-reassign -- toggling a track's enabled flag is the API
      track.enabled = next;
    });
  }, []);

  const stopRecording = useCallback(() => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    if (resumeCountdownTimerRef.current) {
      clearInterval(resumeCountdownTimerRef.current);
      resumeCountdownTimerRef.current = null;
    }
    setResumeCountdownValue(null);
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  }, []);

  // Ticks the elapsed counter (and its ref mirror, used to reschedule the auto-stop after a pause).
  const startElapsedTicker = () => {
    elapsedTimerRef.current = setInterval(
      () => setElapsedSeconds((s) => {
          const next = s + 1;
          elapsedSecondsRef.current = next;
          return next;
        }),
      1000,
    );
  };

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const mimeType = mimeTypeRef.current;
    chunksRef.current = [];

    let recorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (err) {
      setErrorKind(err?.name || 'error');
      setStatus(STATUS.UNSUPPORTED);
      return;
    }
    recorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      // Restart stops the recorder to throw the take away — skip preview and re-run the countdown.
      if (discardingRef.current) {
        discardingRef.current = false;
        chunksRef.current = [];
        beginCountdownRef.current?.();
        return;
      }
      // iOS WebKit: blob types carrying codec params can stall playback; use the bare container
      // there. Elsewhere prefer the recorder's actual mimeType (includes codecs).
      const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
      const blobType = isIOS
        ? (recorder.mimeType || mimeType || 'video/mp4').split(';')[0]
        : (recorder.mimeType || mimeType || 'video/webm');
      const blob = new Blob(chunksRef.current, { type: blobType });
      revokeUrl();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      recordedBlobRef.current = blob;
      setRecordedUrl(url);
      setStatus(STATUS.PREVIEW);
      // Release the camera during preview: iOS refuses to START PLAYBACK of any video while a
      // capture session is live (preview stayed paused at t=0 forever). Re-record re-acquires.
      stopStream();
    };

    recorder.start();
    setPaused(false);
    pausedRef.current = false;
    setElapsedSeconds(0);
    elapsedSecondsRef.current = 0;
    setStatus(STATUS.RECORDING);
    startElapsedTicker();
    autoStopTimerRef.current = setTimeout(stopRecording, maxSeconds * 1000);
  }, [maxSeconds, revokeUrl, stopRecording, stopStream]);

  const beginCountdown = useCallback(() => {
    setCountdownValue(COUNTDOWN_FROM);
    setStatus(STATUS.COUNTDOWN);
    let remaining = COUNTDOWN_FROM;
    countdownTimerRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
        startRecording();
      } else {
        setCountdownValue(remaining);
      }
    }, 1000);
  }, [startRecording]);
  // onstop (defined in startRecording) calls this to restart; a ref breaks the useCallback cycle.
  beginCountdownRef.current = beginCountdown;

  // Pause/resume the in-progress recording. Pausing also freezes the elapsed timer and the auto-stop,
  // which is rescheduled for the remaining time on resume.
  const togglePause = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    if (resumeCountdownTimerRef.current) {
      clearResumeCountdown();
      return;
    }
    if (recorder.state === 'recording') {
      recorder.pause();
      setPaused(true);
      pausedRef.current = true;
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current);
        autoStopTimerRef.current = null;
      }
    } else if (recorder.state === 'paused') {
      // Resume runs the same 3-2-1 as a fresh take (recorder stays paused until it finishes);
      // toggling again mid-countdown cancels it.
      setResumeCountdownValue(COUNTDOWN_FROM);
      let remaining = COUNTDOWN_FROM;
      resumeCountdownTimerRef.current = setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          clearResumeCountdown();
          recorder.resume();
          setPaused(false);
          pausedRef.current = false;
          startElapsedTicker();
          const remainingSeconds = Math.max(0, maxSeconds - elapsedSecondsRef.current);
          autoStopTimerRef.current = setTimeout(stopRecording, remainingSeconds * 1000);
        } else {
          setResumeCountdownValue(remaining);
        }
      }, 1000);
    }
  }, [maxSeconds, stopRecording, clearResumeCountdown]);

  // Throw away the current take and run a fresh 3-2-1 countdown. During recording we stop first
  // (with the discard flag) so onstop re-triggers the countdown instead of showing a preview.
  const restart = useCallback(() => {
    clearTimers();
    setPaused(false);
    pausedRef.current = false;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      discardingRef.current = true;
      recorder.stop();
    } else {
      beginCountdown();
    }
  }, [clearTimers, beginCountdown]);

  const reRecord = useCallback(() => {
    revokeUrl();
    recordedBlobRef.current = null;
    chunksRef.current = [];
    setRecordedUrl(null);
    setElapsedSeconds(0);
    elapsedSecondsRef.current = 0;
    if (streamRef.current) {
      beginCountdown();
    } else {
      // Preview released the camera (iOS playback constraint); grab it again and land on READY.
      acquireRef.current?.();
    }
  }, [revokeUrl, beginCountdown]);

  const confirm = useCallback(() => {
    if (recordedBlobRef.current) {
      onRecorded?.(recordedBlobRef.current, { durationSeconds: elapsedSeconds, mimeType: mimeTypeRef.current });
    }
    onHide?.();
  }, [elapsedSeconds, onRecorded, onHide]);

  // Acquire when opened, tear down when closed. resetState clears the previous session's preview.
  useEffect(() => {
    if (show) {
      acquire();
      return undefined;
    }
    teardownAll();
    resetState();
    return undefined;
  }, [show, acquire, teardownAll, resetState]);

  // Safety net: release the camera even if the parent unmounts the modal without flipping `show`
  // (route change, conversation switch). The single most important line for not leaving the cam on.
  useEffect(() => () => teardownAll(), [teardownAll]);

  // The live <video> node mounts late (react-bootstrap portal), so attach the stream from an effect
  // once both the node and the stream exist — setting srcObject inline in the getUserMedia promise
  // silently no-ops on first open.
  useEffect(() => {
    const node = livePreviewRef.current;
    const live = status === STATUS.READY || status === STATUS.COUNTDOWN || status === STATUS.RECORDING;
    if (node && streamRef.current && live && node.srcObject !== streamRef.current) {
      attachLivePreview(node, streamRef.current);
    }
  }, [status, attachLivePreview]);

  return {
    status,
    countdownValue,
    resumeCountdownValue,
    elapsedSeconds,
    recordedUrl,
    errorKind,
    livePreviewRef,
    cameras,
    mics,
    cameraId,
    micId,
    cameraOn,
    micOn,
    paused,
    selectCamera,
    selectMic,
    toggleCamera,
    toggleMic,
    beginCountdown,
    togglePause,
    restart,
    stopRecording,
    reRecord,
    confirm,
    retry: acquire,
    cancel: onHide,
  };
}
